/**
 * 写真まとめて一括投稿ツール — Storage アップロード (issue #23 → #28 follow-up で地区設定駆動に再設計)。
 *
 * **参加者投稿 (apps/user の uploadNewPhoto) と完全に同一の前処理**を使う:
 *   - shared-ui の `preprocessPhoto` / `generateThumbnail` / `assertPhotoSize`
 *     (browser-image-compression。user / admin で単一ソース共有 = 写真の最大サイズ/解像度/品質が完全一致)。
 *   - 解像度・目標サイズ・品質・受付上限・無圧縮(`uploadOriginal`) はすべて地区 `photoConfig` に従う。
 *   - **issue #28: 管理者投稿は EXIF を強制削除しない**。`uploadOriginal=true` なら原本 (EXIF 保持) を
 *     そのまま保存し、圧縮時は参加者と同様に canvas 再エンコードで EXIF が落ちる (地区設定と一致)。
 *
 * Storage path 規約 (uploadPhoto.ts と同じ): districts/{districtId}/posts/{uploadId}/{original,thumbnail}.jpg
 * 位置情報 (EXIF GPS) は別途 readPhotoExif() が加工前の原本から読み取り済 (exifLocation.ts)。
 */

import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import type { DistrictPhotoConfig } from '@citizen-walk/shared-types';
import { assertPhotoSize, generateThumbnail, preprocessPhoto } from '@citizen-walk/shared-ui';
import { storage } from '../../lib/firebase';

const CONCURRENCY = 5;

/** createImageBitmap で decode できない原本 (HEIC 等) に対する案内メッセージ。 */
export const HEIC_UNDECODABLE_MESSAGE = 'HEIC は JPEG に変換してから取り込んでください';

export interface BulkPhotoUploadResult {
  photoPath: string;
  photoThumbPath: string;
  photoUrl: string | null;
  photoThumbUrl: string | null;
}

export interface BatchUploadResult {
  /** id → アップロード成功分。 */
  ok: Map<string, BulkPhotoUploadResult>;
  /** decode 失敗 (HEIC 等) / アップロード失敗の id とメッセージ。 */
  errors: { id: string; message: string }[];
}

function newUploadId(): string {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `bulk-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * 1 ファイルを地区 photoConfig に従って前処理 → Storage へアップロード。
 * 参加者投稿 (uploadNewPhoto) と同一の preprocessPhoto / generateThumbnail / assertPhotoSize を使う。
 */
export async function uploadOneBulkPhoto(
  districtId: string,
  file: File,
  config?: DistrictPhotoConfig,
  options?: { equirectangular?: boolean },
): Promise<BulkPhotoUploadResult> {
  const [processed, thumb] = await Promise.all([
    // docs/27 §3-2: 360度写真は 4096px まで保持する (1600px では見回し表示に粗すぎる)。
    preprocessPhoto(file, config, { equirectangular: options?.equirectangular === true }),
    generateThumbnail(file),
  ]);
  // 受付上限 (maxUploadSizeMB、既定 10MB) を超えたら throw (参加者投稿と同じ)。
  assertPhotoSize(processed, config);

  const id = newUploadId();
  const basePath = `districts/${districtId}/posts/${id}`;
  const photoPath = `${basePath}/original.jpg`;
  const photoThumbPath = `${basePath}/thumbnail.jpg`;
  // uploadOriginal のときは原本の MIME を維持 (uploadNewPhoto と同じ)。
  const mimeType = config?.uploadOriginal ? file.type || 'image/jpeg' : 'image/jpeg';

  await uploadBytes(ref(storage, photoPath), processed, { contentType: mimeType });
  await uploadBytes(ref(storage, photoThumbPath), thumb, { contentType: 'image/jpeg' });

  const [photoUrl, photoThumbUrl] = await Promise.all([
    getDownloadURL(ref(storage, photoPath)).catch(() => null),
    getDownloadURL(ref(storage, photoThumbPath)).catch(() => null),
  ]);

  return { photoPath, photoThumbPath, photoUrl, photoThumbUrl };
}

/**
 * 複数ファイルを並列度 5 で前処理 + アップロード。
 * 各ファイルは try/catch で隔離するため、1 件の HEIC 失敗がバッチ全体を止めない。
 */
export async function uploadBulkPhotos(
  districtId: string,
  files: { id: string; file: File; equirectangular?: boolean }[],
  config: DistrictPhotoConfig | undefined,
  onProgress?: (done: number, total: number) => void,
): Promise<BatchUploadResult> {
  const ok = new Map<string, BulkPhotoUploadResult>();
  const errors: { id: string; message: string }[] = [];
  const total = files.length;
  let done = 0;

  for (let i = 0; i < files.length; i += CONCURRENCY) {
    const chunk = files.slice(i, i + CONCURRENCY);
    await Promise.all(
      chunk.map(async ({ id, file, equirectangular }) => {
        try {
          ok.set(id, await uploadOneBulkPhoto(districtId, file, config, { equirectangular }));
        } catch (e) {
          errors.push({ id, message: messageFor(e) });
        } finally {
          done++;
          onProgress?.(done, total);
        }
      }),
    );
  }

  return { ok, errors };
}

/**
 * decode 不能エラー (HEIC 等) は案内メッセージに正規化し、それ以外は元メッセージを返す。
 * browser-image-compression / createImageBitmap の HEIC decode 失敗は DOMException
 * (name: 'EncodingError' 等) や 'The source image could not be decoded' 系として現れる。
 */
export function messageFor(e: unknown): string {
  if (e instanceof Error) {
    const m = e.message || '';
    if (
      e.name === 'EncodingError' ||
      e.name === 'InvalidStateError' ||
      /decod/i.test(m) ||
      /source image/i.test(m)
    ) {
      return HEIC_UNDECODABLE_MESSAGE;
    }
    return m || HEIC_UNDECODABLE_MESSAGE;
  }
  return HEIC_UNDECODABLE_MESSAGE;
}
